// src/components/navigation/PartnerSidebar.tsx
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Globe,
  Palette,
  // Settings,
  LogOut,
  Mail,
  Bell,
  Calculator,
  Wallet,
  HelpCircle,
  BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLogo } from '@/contexts/LogoContext';
import MemoizedLogo from '@/components/branding/MemoizedLogo';
import { useSubAccounts } from '@/hooks/useSubAccounts';


const baseNavigation = [
  { name: 'Dashboard', href: '/partner/dashboard', icon: LayoutDashboard },
  { name: 'Sub-Accounts', href: '/partner/sub-accounts', icon: Users },
  { name: 'Payouts', href: '/partner/payouts', icon: Wallet },
  { name: 'Commissions', href: '/partner/commissions', icon: DollarSign },
];

const whitelabelNavigation = [
  { name: 'Domains', href: '/partner/domains', icon: Globe },
  { name: 'Branding', href: '/partner/branding', icon: Palette },
  { name: 'Custom Pricing', href: '/partner/pricing', icon: Calculator },
  { name: 'Help Center Editor', href: '/partner/help-center', icon: HelpCircle },
];

const cobrandedNavigation = [
  { name: 'Subscriptions', href: '/partner/pricing', icon: Calculator },
  { name: 'Tutorials', href: '/partner/tutorials', icon: BookOpen },
];

// Placeholder to satisfy type-checking while Settings tab is hidden
const settingsNavigation: Array<{ name: string; href: string; icon: any }> = [];

export default function PartnerSidebar() {
  const navigate = useNavigate();
  const { signOut, user, profile } = useAuth();
  const { data: accounts } = useSubAccounts();
  const { logoUrl, companyName, isWhiteLabel } = useLogo();
  
  // Use real user data with fallbacks for development
  const userDisplayName = profile?.full_name || user?.email;

  // const subAccountLimit = 50; // TODO: Get this from partner profile/settings

  const handleLogoutClick = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Logout error:', error);
        toast.error('Failed to logout');
      } else {
        toast.success('Logged out successfully');
        navigate('/login');
      }
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  return (
    <div className="flex h-full w-64 flex-col bg-white border-r border-border">
      {/* Logo/Header */}
      <div className="flex items-center justify-center px-4 py-0 border-b border-border">
        <MemoizedLogo
          logoUrl={logoUrl}
          companyName={companyName}
          isWhiteLabel={isWhiteLabel}
          className="h-40 w-auto max-w-full object-contain"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4">
        {/* Main Navigation */}
        <div className="space-y-1">
          {baseNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `w-full group flex items-center px-3 py-2 border-r-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary  border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted border-r-white'
                }`
              }
            >
              <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
              {item.name}
              {accounts && accounts.length > 0 && item.name === 'Sub-Accounts' && (
                <Badge variant="secondary" className="ml-auto text-xs">
                  {accounts.length}
                </Badge>
              )}
            </NavLink>
          ))}
        </div>

        {/* White Label / Co-branded Features */}
        {isWhiteLabel ? (
          <div className="space-y-1 pt-4">
            {whitelabelNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `w-full group flex items-center px-3 py-2 text-sm border-r-2 font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted border-r-white'
                  }`
                }
              >
                <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </div>
        ) : (
          <div className="space-y-1 pt-4">
            {cobrandedNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `w-full group flex items-center px-3 py-2 text-sm border-r-2 font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted border-r-white'
                  }`
                }
              >
                <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </div>
        )}

        {/* Settings - temporarily hidden */}
        {false && (
          <div className="space-y-1 pt-4">
            {settingsNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary border-r-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`
                }
              >
                <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        {/* User Info */}
        <div className="rounded-lg p-3 mb-3 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center text-sm font-medium text-foreground mb-1">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                <span className="truncate">{userDisplayName}</span>
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <Mail className="w-3 h-3 mr-1.5 flex-shrink-0" />
                <span className="truncate">Online</span>
              </div>
            </div>
            <div className="ml-3">
              <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-background/80 transition-all duration-200"
              >
                <Bell className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent/70 transition-all duration-200"
          onClick={handleLogoutClick}
        >
          <LogOut className="mr-3 h-5 w-5" aria-hidden="true" />
          Logout
        </Button>
      </div>
    </div>
  );
}